import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
  InputAdornment,
  Box,
  useMediaQuery,
} from "@material-ui/core";
import { useInventorySearchContext } from "../context";
import { LocationType } from "../../../../utils/constants";
import {
  canRelocateFrom,
  getLocationFullName,
} from "../../../../utils/locationUtils";
import { isQuantityInUnits } from "../../../../utils/loadTypeUtils";
import { handleNumberFieldKeyDown } from "../../../../utils/utils";

export const AisleControl = ({
  handleSave,
  handleAisleChange,
  handleAreaChange,
  handleZoneChange,
  isSaveButtonDisabled,
  scannedQuantity,
  classes,
}) => {
  const { state, dispatch } = useInventorySearchContext();
  const {
    aisles,
    selectedAisle,
    areas,
    zones,
    selectedArea,
    selectedZone,
    selectedLocation,
    selectedInventoryItem,
    disableRelocateValidation,
    inventoryItemBayList,
    originalInventoryItem,
  } = state;
  const [isDisabled, setIsDisabled] = useState();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    if (scannedQuantity > 0) {
      const newInventoryItem = {
        ...selectedInventoryItem,
        quantityOnHand: scannedQuantity,
      };
      dispatch({
        type: "SET_SELECTED_INVENTORY_ITEM",
        payload: newInventoryItem,
      });
    }
  }, [scannedQuantity, selectedInventoryItem, dispatch]);

  useEffect(() => {
    if (inventoryItemBayList) {
      setIsDisabled(
        !canRelocateFrom(
          selectedInventoryItem,
          disableRelocateValidation,
          inventoryItemBayList,
        ),
      );
    }
  }, [selectedInventoryItem, disableRelocateValidation, inventoryItemBayList]);

  const setDisableRelocateValidation = (value) => {
    dispatch({ type: "SET_DISABLE_VALIDATION", payload: value });
  };

  // An unlocated item -- one carrying the UNSPOTTED_VIRTUAL sentinel instead of
  // a real Location id -- reads as "Unspotted" on the from side, which is both
  // true and the same word the rest of the app uses for it.
  const getRelocateText = () => {
    if (
      selectedLocation &&
      selectedLocation.id !== selectedInventoryItem.location?.id
    ) {
      return `Relocate from ${
        selectedInventoryItem.locationFullName || "Unspotted"
      } to ${getLocationFullName(selectedLocation)}`;
    }
    return `Relocate from ${
      selectedInventoryItem.locationFullName || "Unspotted"
    } to ...`;
  };

  const setQuantity = (value) => {
    if (value === "") {
      const newInventoryItem = { ...selectedInventoryItem, quantityOnHand: "" };
      dispatch({
        type: "SET_SELECTED_INVENTORY_ITEM",
        payload: newInventoryItem,
      });
      return;
    }

    const quantity = Number(value || 1);

    if (quantity < 1 || quantity > originalInventoryItem?.quantityOnHand)
      return;

    const newInventoryItem = {
      ...selectedInventoryItem,
      quantityOnHand: quantity,
    };
    dispatch({
      type: "SET_SELECTED_INVENTORY_ITEM",
      payload: newInventoryItem,
    });
  };

  const isQuantityDisabled = () => {
    if (scannedQuantity > 0) return true;
    if (!selectedAisle || !selectedInventoryItem) return true;
    // Every rule below asks what kind of location the stock is leaving. An
    // unlocated item is leaving none, so none of them apply and the crew may
    // move any part of the quantity.
    if (selectedInventoryItem.location?.id === selectedLocation?.id)
      return true;
    if (
      selectedInventoryItem.location?.type === LocationType.STRUCTURED &&
      !selectedInventoryItem.location?.locationLoad?.other?.selected
    )
      return true;
    if (
      selectedInventoryItem.location?.type === LocationType.UNSTRUCTURED &&
      selectedAisle.type === LocationType.STRUCTURED &&
      !selectedAisle?.locationLoad?.other.selected &&
      isQuantityInUnits(selectedInventoryItem.inventory)
    )
      return true;
    return false;
  };

  return (
    <>
      {
        // selectedAisle?.type === LocationType.STRUCTURED && <Grid item xs={12}>
        //     <FormControlLabel
        //         control={
        //             <Switch
        //                 checked={disableRelocateValidation}
        //                 onChange={() => setDisableRelocateValidation(!disableRelocateValidation)}
        //                 color="primary"
        //             />
        //         }
        //         label="Disable validation"
        //     />
        // </Grid>
      }
      <Grid item container xs={12} lg={9} xl={6} spacing={2}>
        <Grid item xs={6} sm={3}>
          <TextField
            select
            id="area"
            label="Area"
            variant="outlined"
            size="small"
            required
            fullWidth
            disabled={isDisabled}
            value={selectedArea ? selectedArea.id : ""}
            onChange={(e) => handleAreaChange(e.target.value)}
          >
            {areas.map((area) => (
              <MenuItem key={area.id} value={area.id}>
                {area.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            select
            id="zone"
            label="Zone"
            variant="outlined"
            size="small"
            required
            fullWidth
            disabled={isDisabled}
            value={selectedZone ? selectedZone.id : ""}
            onChange={(e) => handleZoneChange(e.target.value)}
          >
            {zones.map((zone) => (
              <MenuItem key={zone.id} value={zone.id}>
                {zone.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            select
            id="aisle"
            label="Aisle"
            variant="outlined"
            size="small"
            required
            fullWidth
            disabled={isDisabled}
            value={selectedAisle ? selectedAisle.id : ""}
            onChange={(evt) => handleAisleChange(evt.target.value)}
            autoFocus
          >
            {aisles &&
              [{ id: "", value: "", name: <span>&nbsp;</span> }, ...aisles].map(
                (itm) => (
                  <MenuItem key={itm.id} value={itm.id}>
                    {itm.name}
                  </MenuItem>
                ),
              )}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            id="quantity"
            label="Quantity"
            variant="outlined"
            size="small"
            type="number"
            fullWidth
            inputProps={{ min: 0, max: originalInventoryItem?.quantityOnHand }}
            disabled={isQuantityDisabled()}
            value={
              scannedQuantity > 0
                ? scannedQuantity
                : selectedInventoryItem?.quantityOnHand
            }
            error={!selectedInventoryItem?.quantityOnHand}
            onChange={(evt) => setQuantity(evt.target.value)}
            onKeyDown={handleNumberFieldKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {selectedInventoryItem?.inventory?.inventoryUOM}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSave}
              disabled={
                isSaveButtonDisabled() || !selectedInventoryItem?.quantityOnHand
              }
            >
              Save
            </Button>
            {!isSmall && (
              <Typography
                component="span"
                variant="subtitle1"
                color="textSecondary"
                style={{ marginLeft: 8 }}
              >
                {getRelocateText()}
              </Typography>
            )}
          </Box>
        </Grid>
        {isSmall && (
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="textSecondary">
              {getRelocateText()}
            </Typography>
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
    </>
  );
};
